/**
 * moc-premium-popup.js
 * Mộc Picks — Gen Z Editorial Product Discovery · Mộc By Dylan
 *
 * Custom Element: <moc-premium-popup>
 * Trigger:    configurable delay OR 40% page scroll — whichever comes first
 * Suppression: sessionStorage key "moc_popup_seen" — once per browser session
 * Close:      X button · ESC key · click outside card
 * Mode:       product-first CTA link — no email capture
 */

class MocPremiumPopup extends HTMLElement {
  /** @type {AbortController} */
  #ac = new AbortController();

  /** @type {number|null} */
  #timerId = null;

  /** @type {boolean} */
  #triggered = false;

  connectedCallback() {
    if (this.#shouldSuppress()) return;

    const { signal } = this.#ac;

    // ── Trigger: timer ──────────────────────────────────────────
    const delayMs = parseInt(this.dataset.delay ?? '8000', 10);
    this.#timerId = window.setTimeout(() => this.#trigger(), delayMs);

    // ── Trigger: scroll ─────────────────────────────────────────
    window.addEventListener(
      'scroll',
      () => {
        const scrolled =
          (window.scrollY + window.innerHeight) /
          document.documentElement.scrollHeight;
        if (scrolled >= 0.4) this.#trigger();
      },
      { signal, passive: true }
    );

    // ── Close: X button ─────────────────────────────────────────
    const closeBtn = this.querySelector('[data-ref="closeBtn"]');
    closeBtn?.addEventListener('click', () => this.#close(), { signal });

    // ── Close: CTA link click — persist suppression before navigate ──
    const ctaLink = this.querySelector('[data-ref="ctaLink"]');
    ctaLink?.addEventListener('click', () => this.#persistSeen(), { signal });

    // ── Close: ESC key ──────────────────────────────────────────
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.hasAttribute('open')) this.#close();
      },
      { signal }
    );

    // ── Close: click outside card ────────────────────────────────
    const overlay = this.querySelector('[data-ref="overlay"]');
    overlay?.addEventListener(
      'click',
      (e) => {
        if (e.target === overlay) this.#close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  // ── Private ────────────────────────────────────────────────────

  /** Returns true if the popup was already seen in this browser session. */
  #shouldSuppress() {
    try {
      return sessionStorage.getItem('moc_popup_seen') === '1';
    } catch {
      return false;
    }
  }

  /** Fire once — whichever of timer/scroll wins. */
  #trigger() {
    if (this.#triggered) return;
    this.#triggered = true;

    if (this.#timerId !== null) {
      window.clearTimeout(this.#timerId);
      this.#timerId = null;
    }

    this.#show();
  }

  /** Open the popup. */
  #show() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';

    // Move focus to the primary CTA for accessibility
    window.setTimeout(() => {
      const ctaLink = this.querySelector('[data-ref="ctaLink"]');
      const closeBtn = this.querySelector('[data-ref="closeBtn"]');
      (ctaLink ?? closeBtn)?.focus();
    }, 440);

    // Trap focus within the card
    this.#setupFocusTrap();
  }

  /** Close the popup and persist the suppression timestamp. */
  #close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    this.#persistSeen();
    this.#cleanup();
  }

  /** Mark popup as seen for this session — safe to call on CTA navigation too. */
  #persistSeen() {
    try {
      sessionStorage.setItem('moc_popup_seen', '1');
    } catch {
      // Storage unavailable — non-fatal
    }
  }

  /** Clean up all event listeners and timers. */
  #cleanup() {
    this.#ac.abort();
    if (this.#timerId !== null) {
      window.clearTimeout(this.#timerId);
      this.#timerId = null;
    }
  }

  /**
   * Minimal focus trap — keeps Tab/Shift+Tab cycling within the popup card.
   * Uses the existing AbortController signal so it tears down on close.
   */
  #setupFocusTrap() {
    const { signal } = this.#ac;
    const card = this.querySelector('.moc-popup__card');
    if (!card) return;

    const FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Tab' || !this.hasAttribute('open')) return;

        const focusable = [...card.querySelectorAll(FOCUSABLE)];
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      },
      { signal }
    );
  }
}

customElements.define('moc-premium-popup', MocPremiumPopup);
